import type { IntercomStatic } from "@/declare/windowIntercom";
import type { Role } from "@/model/user";
import { useMessengerStore } from "@/store/messenger";
import { whenChildElementWithClass } from "@/utils/dom/dom";

import type { LicenseInfo, UserInfo } from "../types";
import { articles } from "./intercomArticles";

interface IntercomConfig {
  id: string;
  api: string;
}

export function initIntercom(config: IntercomConfig, license: LicenseInfo, user: UserInfo) {
  resetIntercom();
  window.loadIntercom(config.id, config.api);

  // show the custom intercom launcher only when the default launcher would be shown (it's in the DOM but made invisible by CSS)
  // the reason is that the default launcher can be configured to appear only in certain conditions (e.g. user role)
  // custom launcher cannot be configured like this.
  // Remove this code when we don't need this function anymore (because intercom launcher should always be shown)
  ifDefaultLauncherShown(() => useMessengerStore().setShow(true));

  window.Intercom("boot", {
    custom_launcher_selector: ".messenger-item",
    email: user.email,
    name: user.name,
    user_id: user.id,
    user_hash: user.hash,
    role: intercomRoles[user.role],
    language_override: user.preferredLanguage,
    avatar: { type: "avatar", image_url: user.imageUrl || "" },
    company: { company_id: user.company, name: user.company, plan: license.plan },
  });
}

export function resetIntercom() {
  window.Intercom = null as unknown as IntercomStatic; // delete existing intercom (dummy) instance
}

/**
 * Returns true if the Intercom command queue is available.
 * Once present, calls to window.Intercom() are queued and replayed on boot.
 */
function isIntercomReady(): boolean {
  return !!window.Intercom && "q" in window.Intercom;
}

/**
 * Opens an Intercom help article by id. If the Intercom Messenger is loaded, the article opens
 * inside the in-app Messenger; otherwise it falls back to the public help-center page in a new tab.
 */
export function openHelpArticle(articleId: keyof typeof articles) {
  const article = articles[articleId];
  if (!article) return;

  if (isIntercomReady()) {
    window.Intercom("showArticle", article.articleId);
  } else {
    window.open(article.url, "_blank", "noopener,noreferrer");
  }
}

/**
 * Calls the callback when the Intercom SDK has finished loading
 * We check for the "q" property (the command queue) which the Intercom snippet
 * creates in loadIntercom. Once present, calls are queued and replayed on boot.
 */
export function onIntercomQueueReady(cb: () => void) {
  const interval = setInterval(() => {
    if (isIntercomReady()) {
      clearInterval(interval);
      cb();
    }
  }, 500);
  setTimeout(() => clearInterval(interval), 10_000);
}

function ifDefaultLauncherShown(action: () => void) {
  whenChildElementWithClass(document.body, "intercom-lightweight-app", (app) =>
    whenChildElementWithClass(app, "intercom-launcher", action),
  );
}

const intercomRoles: Record<Role, string> = {
  observer: "Observer",
  user: "Member",
  admin: "Admin",
  planning_interval_admin: "PI Admin",
};
